import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Radio,
  Space,
  Typography,
  message,
  Spin,
  Divider,
  Row,
  Col,
  Alert
} from 'antd';
import {
  UserAddOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  UserOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { userService } from '../../services';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const UserForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams(); // For edit mode

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [userData, setUserData] = useState(null);

  // Determine if we're in edit mode
  useEffect(() => {
    const path = window.location.pathname;
    setIsEditMode(path.includes('/edit/') && id);
  }, [id]);

  // Load available roles and current user role on component mount
  useEffect(() => {
    loadAvailableRoles();
    loadCurrentUserRole();

    // Load user data if in edit mode
    if (isEditMode && id) {
      loadUserData();
    }
  }, [isEditMode, id]);

  const loadAvailableRoles = async () => {
    try {
      // For now, set default roles based on API documentation
      // In a real app, this might come from an API endpoint
      setAvailableRoles(['admin', 'manager', 'stock', 'member', 'customer']);
    } catch (error) {
      ////console.error('Error loading available roles:', error);
    }
  };

  const loadCurrentUserRole = () => {
    // Get current user role from localStorage or context
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      try {
        const user = JSON.parse(userInfo);
        setCurrentUserRole(user.role);
      } catch (error) {
        ////console.error('Error parsing user info:', error);
      }
    }
  };

  const loadUserData = async () => {
    try {
      setInitialLoading(true);
      const response = await userService.getDetail(id);

      if (response.success) {
        const user = response.data.user;
        setUserData(user);

        // Populate form with existing data
        form.setFieldsValue({
          username: user.username,
          email: user.email,
          fullName: user.full_name,
          role: user.role,
          NgaySinh: user.NgaySinh ? dayjs(user.NgaySinh) : null,
          GioiTinh: user.GioiTinh,
          SDT: user.SDT,
        });
      } else {
        message.error('Không thể tải thông tin người dùng');
        navigate('/users/list');
      }
    } catch (error) {
      ////console.error('Error loading user data:', error);
      message.error('Có lỗi xảy ra khi tải thông tin người dùng');
      navigate('/users/list');
    } finally {
      setInitialLoading(false);
    }
  };

  // Get available roles based on current user permissions
  const getFilteredRoles = () => {
    if (currentUserRole === 'admin') {
      // Admin can create all roles
      return availableRoles;
    } else if (currentUserRole === 'manager') {
      // Manager cannot create admin or manager roles
      return availableRoles.filter(role => role !== 'admin' && role !== 'manager');
    }
    return [];
  };

  // Get role display name
  const getRoleDisplayName = (role) => {
    const roleNames = {
      'admin': 'Quản trị viên',
      'manager': 'Quản lý',
      'stock': 'Nhân viên kho',
      'member': 'Thành viên',
      'customer': 'Khách hàng'
    };
    return roleNames[role] || role;
  };

  // Get role color
  const getRoleColor = (role) => {
    const roleColors = {
      'admin': 'red',
      'manager': 'orange',
      'stock': 'blue',
      'member': 'green',
      'customer': 'purple'
    };
    return roleColors[role] || 'default';
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Format date if provided
      const formattedValues = {
        ...values,
        NgaySinh: values.NgaySinh ? values.NgaySinh.format('YYYY-MM-DD') : null,
      };

      let response;
      if (isEditMode) {
        // Update existing user
        response = await userService.update(id, formattedValues);
      } else {
        // Create new user
        response = await userService.addUser(formattedValues);
      }

      if (response.success) {
        const action = isEditMode ? 'cập nhật' : 'tạo';
        message.success(`Đã ${action} người dùng ${response.data.user.username} thành công!`);
        navigate('/users/list');
      } else {
        message.error(response.message || `Có lỗi xảy ra khi ${isEditMode ? 'cập nhật' : 'tạo'} người dùng`);
      }
    } catch (error) {
      ////console.error(`Error ${isEditMode ? 'updating' : 'creating'} user:`, error);
      if (error.response?.status === 401) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Bạn không có quyền thực hiện thao tác này');
      } else if (error.response?.status === 400) {
        message.error(error.response.data?.message || 'Dữ liệu không hợp lệ');
      } else {
        message.error(`Có lỗi xảy ra khi ${isEditMode ? 'cập nhật' : 'tạo'} người dùng`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission error
  const handleSubmitError = (errorInfo) => {
    ////console.log('Form validation failed:', errorInfo);
    message.error('Vui lòng kiểm tra lại thông tin đã nhập');
  };

  // Custom validation rules
  const validationRules = {
    username: [
      { required: true, message: 'Vui lòng nhập tên đăng nhập' },
      { min: 3, message: 'Tên đăng nhập phải có ít nhất 3 ký tự' },
      { max: 50, message: 'Tên đăng nhập không được vượt quá 50 ký tự' },
      {
        pattern: /^[a-zA-Z0-9_]+$/,
        message: 'Tên đăng nhập chỉ được chứa chữ cái, số và dấu gạch dưới'
      }
    ],
    password: isEditMode ? [] : [ // Password not required for edit mode
      { required: true, message: 'Vui lòng nhập mật khẩu' },
      { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' },
      { max: 100, message: 'Mật khẩu không được vượt quá 100 ký tự' }
    ],
    email: [
      { required: true, message: 'Vui lòng nhập địa chỉ email' },
      { type: 'email', message: 'Địa chỉ email không hợp lệ' }
    ],
    fullName: [
      { required: true, message: 'Vui lòng nhập họ và tên' },
      { min: 2, message: 'Họ và tên phải có ít nhất 2 ký tự' },
      { max: 100, message: 'Họ và tên không được vượt quá 100 ký tự' }
    ],
    role: [
      { required: true, message: 'Vui lòng chọn vai trò' }
    ],
    SDT: [
      {
        pattern: /^[0-9]{10,12}$/,
        message: 'Số điện thoại phải có 10-12 chữ số'
      }
    ]
  };

  const filteredRoles = getFilteredRoles();

  if (initialLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Đang tải thông tin người dùng...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/users/list')}
          style={{ marginBottom: 16 }}
        >
          Quay lại danh sách
        </Button>

        <Title level={2} style={{ margin: 0 }}>
          {isEditMode ? <EditOutlined /> : <UserAddOutlined />}
          {isEditMode ? 'Chỉnh sửa người dùng' : 'Thêm người dùng mới'}
        </Title>
        <Text type="secondary">
          {isEditMode
            ? 'Cập nhật thông tin người dùng trong hệ thống'
            : 'Tạo tài khoản người dùng mới với thông tin chi tiết'
          }
        </Text>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <UserOutlined />
                Thông tin người dùng
              </Space>
            }
            loading={loading}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              onFinishFailed={handleSubmitError}
              autoComplete="off"
            >
              {/* Required Information */}
              <Divider orientation="left">Thông tin bắt buộc</Divider>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Tên đăng nhập"
                    name="username"
                    rules={validationRules.username}
                    tooltip="Tên đăng nhập phải có 3-50 ký tự, chỉ chứa chữ cái, số và dấu gạch dưới"
                  >
                    <Input
                      prefix={<UserOutlined />}
                      placeholder="Nhập tên đăng nhập"
                      autoComplete="username"
                      disabled={isEditMode} // Username cannot be changed in edit mode
                    />
                  </Form.Item>
                </Col>

                {!isEditMode && (
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Mật khẩu"
                      name="password"
                      rules={validationRules.password}
                      tooltip="Mật khẩu phải có ít nhất 6 ký tự"
                    >
                      <Input.Password
                        prefix={<LockOutlined />}
                        placeholder="Nhập mật khẩu"
                        autoComplete="new-password"
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={validationRules.email}
                  >
                    <Input
                      prefix={<MailOutlined />}
                      placeholder="Nhập địa chỉ email"
                      autoComplete="email"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Họ và tên"
                    name="fullName"
                    rules={validationRules.fullName}
                  >
                    <Input
                      placeholder="Nhập họ và tên đầy đủ"
                      autoComplete="name"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Vai trò"
                name="role"
                rules={validationRules.role}
                tooltip={
                  currentUserRole === 'manager'
                    ? 'Manager không được phép tạo tài khoản Admin hoặc Manager'
                    : null
                }
              >
                <Select placeholder="Chọn vai trò người dùng">
                  {filteredRoles.map(role => (
                    <Option key={role} value={role}>
                      <Space>
                        <span>{getRoleDisplayName(role)}</span>
                        <span style={{
                          color: getRoleColor(role),
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ({role.toUpperCase()})
                        </span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Optional Information */}
              <Divider orientation="left">Thông tin bổ sung (tùy chọn)</Divider>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Ngày sinh"
                    name="NgaySinh"
                  >
                    <DatePicker
                      placeholder="Chọn ngày sinh"
                      style={{ width: '100%' }}
                      disabledDate={(current) => {
                        // Cannot select future dates
                        return current && current > dayjs().endOf('day');
                      }}
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Giới tính"
                    name="GioiTinh"
                  >
                    <Radio.Group>
                      <Radio value="Nam">Nam</Radio>
                      <Radio value="Nữ">Nữ</Radio>
                      <Radio value="Khác">Khác</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Số điện thoại"
                name="SDT"
                rules={validationRules.SDT}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="Nhập số điện thoại (10-12 chữ số)"
                  autoComplete="tel"
                />
              </Form.Item>

              {/* Form Actions */}
              <Divider />

              <Form.Item style={{ marginBottom: 0 }}>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={loading}
                    size="large"
                  >
                    {isEditMode ? 'Cập nhật người dùng' : 'Tạo người dùng'}
                  </Button>

                  <Button
                    onClick={() => navigate('/users/list')}
                    size="large"
                  >
                    Hủy
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          {/* Information Panel */}
          <Card title="Hướng dẫn" size="small">
            <Space direction="vertical" size="small">
              <Text strong>Quy tắc {isEditMode ? 'cập nhật' : 'tạo tài khoản'}:</Text>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                <li>Tên đăng nhập: 3-50 ký tự, chỉ chứa chữ cái, số, dấu gạch dưới</li>
                {!isEditMode && <li>Mật khẩu: ít nhất 6 ký tự</li>}
                <li>Email: phải là địa chỉ email hợp lệ</li>
                <li>Họ và tên: 2-100 ký tự</li>
                <li>Số điện thoại: 10-12 chữ số (tùy chọn)</li>
              </ul>

              <Divider style={{ margin: '12px 0' }} />

              <Text strong>Phân quyền:</Text>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                <li><Text type="danger">Admin:</Text> Có thể {isEditMode ? 'cập nhật' : 'tạo'} tất cả loại tài khoản</li>
                <li><Text type="warning">Manager:</Text> Không thể {isEditMode ? 'cập nhật' : 'tạo'} Admin/Manager</li>
              </ul>
            </Space>
          </Card>

          {/* Role Information */}
          <Card title="Thông tin vai trò" size="small" style={{ marginTop: 16 }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {filteredRoles.map(role => (
                <div key={role} style={{
                  padding: '8px 12px',
                  border: `1px solid ${getRoleColor(role)}20`,
                  borderRadius: '4px',
                  backgroundColor: `${getRoleColor(role)}10`
                }}>
                  <Text strong style={{ color: getRoleColor(role) }}>
                    {getRoleDisplayName(role)}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {role.toUpperCase()}
                  </Text>
                </div>
              ))}
            </Space>
          </Card>

          {/* Current User Info (in edit mode) */}
          {isEditMode && userData && (
            <Card title="Thông tin hiện tại" size="small" style={{ marginTop: 16 }}>
              <Space direction="vertical" size="small">
                <div>
                  <Text strong>Trạng thái:</Text>
                  <br />
                  <Text type="secondary">{userData.status}</Text>
                </div>
                <div>
                  <Text strong>Ngày tạo:</Text>
                  <br />
                  <Text type="secondary">
                    {dayjs(userData.created_at).format('DD/MM/YYYY HH:mm')}
                  </Text>
                </div>
                <div>
                  <Text strong>Cập nhật cuối:</Text>
                  <br />
                  <Text type="secondary">
                    {dayjs(userData.updated_at).format('DD/MM/YYYY HH:mm')}
                  </Text>
                </div>
              </Space>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default UserForm;
